import { type Error, getApiErrorMessage } from '#imports'
import { type Ref, watch } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api'
import {
  type ThresholdKind,
  getShippingValidationThresholds,
} from '@backmarket/http-api/src/api-specs-shipping/thresholds'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import type { ShippingCategoryGroupMetadata } from '../../../CategoryList.types'
import { transformShippingValidationThresholds } from '../RuleDrawer.utils'

export function useHttpGetThresholds(
  shippingCategoryGroupId: Ref<ShippingCategoryGroupMetadata['id']>,
  markets: Ref<MarketCountryCode[]>,
  thresholdKinds: ThresholdKind[],
  triggerError = false,
) {
  const { openErrorToast } = useTheToast()
  const { publicId: sellerId } = useSellerInfoStore()

  const { data, error, execute } = useHttpFetch(
    getShippingValidationThresholds,
    {
      pathParams: { sellerId },
      queryParams: {
        shippingCategoryGroupId,
        markets,
        thresholdKinds,
      },
      transform: transformShippingValidationThresholds,
      immediate: false,
      watch: false,
    },
  )

  watch(
    [shippingCategoryGroupId, markets],
    async () => {
      if (markets.value.length) {
        await execute()
      }
    },
    { immediate: true },
  )

  if (triggerError && error.value) {
    openErrorToast({
      content: getApiErrorMessage(error.value as unknown as Error),
    })
  }

  return { data, error }
}
